import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import IPRModal from './IPRModal'
import './ProfilePage.css'
// IPRViewer больше не используется
import { useIprProgress } from '../hooks/useIprProgress'
import { supabase } from '../supabase/client'
import IPRProgress from './IPRProgress'
import TalentIcon from './TalentIcon'

// Функция для генерации цвета таланта на основе его ID
const getColorForTalent = (talentId) => {
  // Массив ярких цветов
  const colors = [
    '#10BDC8', // Основной цвет
    '#1cc88a', // Зеленый
    '#4e73df', // Синий
    '#f6c23e', // Желтый
    '#e74a3b', // Красный
    '#36b9cc', // Голубой
    '#6f42c1', // Фиолетовый
    '#fd7e14', // Оранжевый
  ]

  // Используем остаток от деления ID на количество цветов
  const colorIndex = (talentId - 1) % colors.length
  return colors[colorIndex]
}

const ProfilePage = () => {
  // State for current user's uuid
  const [currentUserUuid, setCurrentUserUuid] = useState<string | null>(null)

  useEffect(() => {
    const fetchUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      setCurrentUserUuid(user?.id || null)
    }
    fetchUser()
  }, [])

  // Получаем userId из URL параметров (если есть)
  const { userId } = useParams()
  const navigate = useNavigate()

  // Состояния для данных профиля и вкладок
  const [activeTab, setActiveTab] = useState('talents')
  const [profileData, setProfileData] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedTalent, setSelectedTalent] = useState(null)
  const [showIPRModal, setShowIPRModal] = useState(false)
  const [currentIPR, setCurrentIPR] = useState('')
  const [isCreatingIPR, setIsCreatingIPR] = useState(false)
  const [checkingIPR, setCheckingIPR] = useState(false)

  // Состояния для отслеживания прогресса ИПР
  const [iprProgressData, setIprProgressData] = useState([])

  // Состояние для отслеживания развернутых талантов (для отображения чек-листов)
  const [expandedTalents, setExpandedTalents] = useState({})

  // Функции для работы с ИПР
  const handleCreateIPR = (talent) => {
    setSelectedTalent(talent)
    setCheckingIPR(true) // Показываем индикатор загрузки

    // Проверяем, есть ли уже ИПР для этого таланта И для этого профиля
    checkExistingIPR(talent.id)
  }

  // Вспомогательная функция для проверки, есть ли у таланта созданный ИПР
  const hasCreatedIPR = (talentId) => {
    // Проверяем, есть ли данные о созданных ИПР и есть ли ИПР для этого таланта
    return (
      profileData &&
      profileData.createdIPRs &&
      profileData.createdIPRs[talentId] &&
      Array.isArray(profileData.createdIPRs[talentId])
    )
  }

  const iprHook = useIprProgress()

  const checkExistingIPR = async (talentId) => {
    try {
      const result = await iprHook.getProgress(userId)
      if (!result.success) throw new Error('Failed to load progress')
      const progressData = result.progress || {}
      if (progressData[talentId]) {
        // IPR exists, navigate to checklist
        setTimeout(() => {
          navigate(`/checklist/${userId}/${talentId}`)
        }, 300)
      } else {
        // IPR does not exist, show modal
        setShowIPRModal(true)
      }
    } catch (error) {
      console.error('Error checking IPR existence:', error)
      setShowIPRModal(true) // Fall back to showing modal on error
    } finally {
      setCheckingIPR(false)
    }
  }

  const handleIPRSubmit = async (feedback) => {
    if (!selectedTalent) return

    setIsCreatingIPR(true)
    console.log('Отправка запроса на создание ИПР, талант:', selectedTalent)

    try {
      // Replace API call with hook invocation
      const result = await iprHook.getOrCreateIPR(userId, parseInt(selectedTalent.id), feedback)
      if (!result.success) throw new Error('Failed to get or create IPR from hook')
      const data = { ipr: result.ipr, isNew: result.isNew }

      // Сохраняем содержимое IPR и закрываем модальное окно
      setCurrentIPR(data.ipr)
      setShowIPRModal(false)

      // Шаг 2: Автоматически создаем чек-лист на основе созданного ИПР
      console.log('ИПР успешно создан, создаем чек-лист...')
      try {
        // Создаем пустой чек-лист (он будет заполнен автоматически при первом рендеринге)
        await iprHook.saveProgress(userId, selectedTalent.id, [])
        console.log(`Прогресс ИПР для таланта ${selectedTalent.id} успешно сохранен в базе данных`)
      } catch (error) {
        console.warn('Не удалось сохранить прогресс в базу данных:', error)
        console.log('Прогресс может быть сохранен только локально или обработан иначе')
      }

      // Шаг 3: Обновляем локальное состояние
      // Обновляем информацию о созданных ИПР в профиле
      const updatedProfileData = { ...profileData }
      if (!updatedProfileData.createdIPRs) {
        updatedProfileData.createdIPRs = {}
      }
      // Создаем запись о чек-листе для этого таланта
      updatedProfileData.createdIPRs[selectedTalent.id] = []
      setProfileData(updatedProfileData)

      // Шаг 4: Перенаправляем на страницу чек-листа
      console.log('Перенаправляем на страницу чек-листа для таланта', selectedTalent.id)

      // Немного задержки для обновления состояния
      setTimeout(() => {
        navigate(`/checklist/${userId}/${selectedTalent.id}`)
      }, 300)
    } catch (error) {
      console.error('Ошибка при создании ИПР:', error)
      alert('Произошла ошибка при создании ИПР. Пожалуйста, попробуйте еще раз.')
    } finally {
      setIsCreatingIPR(false)
    }
  }

  const handleCloseIPRModal = () => {
    setShowIPRModal(false)
    setSelectedTalent(null)
  }

  // Удалена неиспользуемая функция handleCloseIPRViewer

  // Функция для перехода на страницу чек-листа
  const navigateToChecklist = (talentId) => {
    if (currentUserUuid !== profile?.uuid) {
      return
    }

    // Вместо переключения отображения чек-листа, перенаправляем на отдельную страницу
    navigate(`/checklist/${userId}/${talentId}`)
  }

  // Функция для обновления прогресса выполнения ИПР
  const handleIPRProgressUpdate = (talentId, progress) => {
    setIprProgressData((prevData) => {
      // Ищем существующий запись по talentId
      const existingIndex = prevData.findIndex((item) => item.talentId === talentId)

      if (existingIndex >= 0) {
        // Обновляем существующую запись
        const updatedData = [...prevData]
        updatedData[existingIndex] = {
          ...updatedData[existingIndex],
          progress,
        }
        return updatedData
      } else {
        // Создаем новую запись
        const talent = profileData?.talents?.find((t) => t.id === talentId)
        return [
          ...prevData,
          {
            talentId,
            talentName: talent ? talent.name : `Талант ${talentId}`,
            progress,
          },
        ]
      }
    })
  }

  // Функция загрузки данных профиля (вынесена из useEffect, чтобы можно было вызывать при необходимости)
  const fetchProfileData = async () => {
    try {
      setLoading(true)
      console.log('Загрузка профиля, userId:', userId)

      // Direct Supabase call to fetch all IPR profiles for the user
      const { data: profile, error: iprError } = await supabase
        .from('ipr_profiles')
        .select('*')
        .eq('userId', userId)
        .single()
      if (iprError) {
        console.error('Ошибка при direct Supabase call for IPR data:', iprError)
        throw iprError
      }
      if (!profile || profile.length === 0) {
        throw new Error('IPR данные не найдены для пользователя')
      }
      console.log('Получены данные профиля:', profile)
      setProfile(profile)

      const profileData = JSON.parse(profile.profileData)

      // Загружаем информацию о созданных ИПР (если есть)
      try {
        // Запрашиваем информацию о прогрессе для выявления существующих ИПР
        const result = await iprHook.getProgress(userId)
        if (!result.success) throw new Error('Failed to load progress from hook')
        const iprData = result

        // Обновляем данные профиля с информацией о существующих ИПР
        if (iprData.success && iprData.progress) {
          console.log('Найдены созданные ИПР:', Object.keys(iprData.progress).length)
          profileData.createdIPRs = iprData.progress

          // Инициализируем прогресс ИПР для статистики
          const progressData = []
          if (profileData.talents && profileData.talents.length > 0) {
            // Для каждого таланта с созданным ИПР добавляем информацию о прогрессе
            profileData.talents.forEach((talent) => {
              const talentId = talent.id
              if (iprData.progress[talentId]) {
                // Вычисляем прогресс выполнения ИПР
                const tasks = iprData.progress[talentId]
                const completedTasks = tasks.filter((t) => t.completed).length
                const totalTasks = tasks.length
                const progressPercent =
                  totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0

                // Добавляем в массив прогресса
                progressData.push({
                  talentId,
                  talentName: talent.name,
                  progress: progressPercent,
                })
              }
            })

            // Обновляем состояние прогресса ИПР
            setIprProgressData(progressData)
          }
        }
        setProfileData(profileData)
      } catch (iprError) {
        console.warn('Ошибка при загрузке информации об ИПР:', iprError)
        // Продолжаем без информации об ИПР
      }
    } catch (err) {
      console.error('Ошибка при загрузке профиля:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // Загрузка данных профиля при монтировании компонента
  useEffect(() => {
    if (userId) {
      fetchProfileData()
    }
  }, [userId])

  // Очистка данных профиля при размонтировании компонента
  useEffect(() => {
    return () => {
      // Очищаем данные при уходе со страницы
      setProfileData(null)
      setSelectedTalent(null)
      setCurrentIPR('')
    }
  }, [])

  // Если данные загружаются
  if (loading) {
    return (
      <div className="profile-container">
        <div className="profile-loading">
          <div className="loader"></div>
          <p>Загрузка профиля...</p>
        </div>
      </div>
    )
  }

  // Если произошла ошибка
  if (error || !profileData) {
    return (
      <div className="profile-container">
        <div className="profile-error">
          <h2>Не удалось загрузить профиль</h2>
          <p>{error || 'Неизвестная ошибка'}</p>
          <button className="btn" onClick={() => navigate('/dashboard')}>
            Вернуться в личный кабинет
          </button>
        </div>
      </div>
    )
  }

  // Деструктуризация данных профиля для удобства
  const { talents, statistics = [] } = profileData

  // Расчет доли верифицированных талантов
  const verifiedTalents = talents.filter((t) => t.level >= 3).length
  const verifiedPercentage = talents.length ? (verifiedTalents / talents.length) * 100 : 0

  // Дополняем статистику
  const enhancedStatistics = [
    ...statistics,
    {
      value: `${verifiedPercentage.toFixed(1)}%`,
      label: 'Высокоразвитые таланты',
    },
  ]

  // Функция для генерации PDF профиля с улучшенным форматированием
  const handleExportProfile = () => {
    // Добавляем текущую дату в атрибут для отображения в колонтитуле
    const profileContainer = document.querySelector('.profile-container')
    const today = new Date()
    const dateStr = today.toLocaleDateString('ru-RU')
    profileContainer.setAttribute('data-date', dateStr)

    // Временно добавляем класс для печати
    profileContainer.classList.add('printing')

    // Используем window.print() для стандартного диалога печати
    window.print()

    // Удаляем класс после печати
    setTimeout(() => {
      profileContainer.classList.remove('printing')
    }, 1000)
  }

  return (
    <div className="profile-container">
      {/* Профиль */}
      <div className="profile-header">
        <div className="profile-content">
          <div className="profile-section">
            {/* Аватар */}
            <div className="profile-avatar">
              {profile.firstName && profile.lastName
                ? profile.firstName[0] + profile.lastName[0]
                : '👤'}
            </div>

            {/* Информация */}
            <div className="profile-info">
              <h1 className="profile-name">
                {profile.firstName} {profile.lastName}
              </h1>
              <p className="profile-role">
                {profile.position} {profile.company ? `• ${profile.company}` : ''}
              </p>
              <p className="profile-bio">
                Профиль бизнес-талантов, основанный на врожденном потенциале. Показывает{' '}
                {talents.length} ключевых талантов и их текущий уровень развития.
              </p>

              <div className="profile-actions">
                <button className="btn-primary" onClick={() => navigate('/dashboard')}>
                  Вернуться в личный кабинет
                </button>
                <button className="btn-outline" onClick={handleExportProfile}>
                  Экспорт профиля
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => navigate(`/full-report/${userId}`)}
                >
                  Полный отчет
                </button>
              </div>
            </div>
          </div>

          {/* Навигация */}
          <div className="profile-tabs">
            <div
              className={`profile-tab ${activeTab === 'talents' ? 'active' : ''}`}
              onClick={() => setActiveTab('talents')}
            >
              Таланты
            </div>
            <div
              className={`profile-tab ${activeTab === 'statistics' ? 'active' : ''}`}
              onClick={() => setActiveTab('statistics')}
            >
              Статистика
            </div>
          </div>
        </div>
      </div>

      {/* Контент */}
      <div className="profile-content">
        {activeTab === 'talents' && (
          <div>
            <h2 className="section-title">Таланты и уровень развития</h2>

            <div className="talents-grid">
              {talents.map((talent) => {
                // Проверяем, создан ли для этого таланта ИПР для текущего профиля
                const hasIPR =
                  profileData.createdIPRs &&
                  profileData.createdIPRs[talent.id] &&
                  Array.isArray(profileData.createdIPRs[talent.id])
                // Находим прогресс для этого таланта, если есть
                const iprProgress = iprProgressData.find((item) => item.talentId === talent.id)

                return (
                  <div key={talent.id} className={`talent-card ${hasIPR ? 'has-ipr' : ''}`}>
                    <div className="talent-header">
                      <TalentIcon talentId={talent.id} name={talent.name} />
                      <div className="talent-name-container">
                        <div className="talent-name">
                          {talent.name}
                          {/* Индикатор создания ИПР */}
                          {hasIPR && (
                            <span className="ipr-indicator" title="Создан план развития">
                              <i className="fas fa-check-circle"></i>
                            </span>
                          )}
                        </div>
                        <div className="talent-role">{talent.role}</div>
                      </div>
                      <div className={`talent-badge ${talent.level >= 4 ? 'verified' : ''}`}>
                        {talent.level}/5
                      </div>
                    </div>

                    <div className="talent-progress">
                      <div className="talent-progress-bar">
                        <div
                          className="talent-progress-fill"
                          style={{
                            width: `${talent.level * 20}%`,
                            backgroundColor: talent.level >= 4 ? '#10BDC8' : '#bbb',
                          }}
                        ></div>
                      </div>
                      <div className="talent-progress-text">
                        <span>Развитость таланта</span>
                        <span>{talent.level * 20}%</span>
                      </div>
                    </div>

                    <div className="talent-satisfaction">
                      <div className="satisfaction-label">Удовлетворенность:</div>
                      <div className="satisfaction-indicators">
                        {[1, 2, 3, 4, 5].map((level) => (
                          <span
                            key={level}
                            className={`satisfaction-dot ${level <= talent.satisfaction ? 'active' : ''}`}
                          ></span>
                        ))}
                      </div>
                    </div>

                    <div className="talent-description">{talent.description}</div>

                    {talent.skills && <div className="talent-skills">{talent.skills}</div>}

                    {/* Чек-лист и прогресс выполнения ИПР (если есть) */}
                    {hasIPR && (
                      <div className="talent-ipr-section">
                        <div
                          className="talent-ipr-header"
                          onClick={() => navigateToChecklist(talent.id)}
                        >
                          <div className="ipr-progress-label">
                            <span className="ipr-progress-title">План развития</span>
                            <span className="ipr-progress-percent">
                              {iprProgress ? iprProgress.progress : 0}%
                            </span>
                          </div>
                          <div className="mini-progress-bar">
                            <div
                              className="mini-progress-fill"
                              style={{ width: `${iprProgress ? iprProgress.progress : 0}%` }}
                            ></div>
                          </div>
                        </div>
                        {/* Чек-лист теперь отображается на отдельной странице */}
                      </div>
                    )}

                    {/* Кнопка для создания ИПР или показа чек-листа */}
                    {profile?.uuid === currentUserUuid && (
                      <div className="talent-actions">
                        {/* Кнопки действий в зависимости от состояния IPR */}
                        {hasIPR || hasCreatedIPR(talent.id) ? (
                          /* Если IPR уже создан, показываем кнопку для перехода к чек-листу */
                          <button
                            className="btn-view-ipr"
                            onClick={() => navigateToChecklist(talent.id)}
                          >
                            <i className="fas fa-tasks"></i>
                            {'Открыть план развития'}
                          </button>
                        ) : (
                          /* Если IPR еще не создан, показываем кнопку для его создания */

                          <button
                            className={`btn-create-ipr ${checkingIPR && selectedTalent?.id === talent.id ? 'loading' : ''}`}
                            onClick={() => handleCreateIPR(talent)}
                            disabled={checkingIPR || isCreatingIPR}
                          >
                            {checkingIPR && selectedTalent?.id === talent.id ? (
                              <>
                                <span className="button-spinner"></span>
                                {'Загрузка плана...'}
                              </>
                            ) : (
                              <>
                                <i className="fas fa-plus-circle"></i>
                                {'Создать план развития'}
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {activeTab === 'statistics' && (
          <div>
            <h2 className="section-title">Статистика талантов</h2>

            <div className="stats-card">
              <div className="stats-container">
                {enhancedStatistics.map((stat, index) => (
                  <div key={index} className="stat-item">
                    <div className="stat-value">{stat.value}</div>
                    <div className="stat-label">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="stats-chart">
                <h3>Отчет по выполнению планов развития</h3>
                <IPRProgress iprs={iprProgressData} />

                {/* Список чек-листов только для талантов, у которых создан ИПР */}
                <div className="ipr-summary">
                  <h4>Планы развития талантов</h4>

                  {/* Фильтруем таланты, чтобы показать только те, у которых есть созданный ИПР */}
                  {iprProgressData.length > 0 ? (
                    <div className="ipr-summary-list">
                      {iprProgressData.map((ipr) => (
                        <div key={ipr.talentId} className="ipr-summary-item">
                          <div className="ipr-summary-talent">
                            <span className="ipr-summary-talent-name">{ipr.talentName}</span>
                            <span className="ipr-summary-talent-percent">{ipr.progress}%</span>
                          </div>
                          <div className="ipr-summary-progress-bar">
                            <div
                              className="ipr-summary-progress-fill"
                              style={{ width: `${ipr.progress}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                      <div className="ipr-summary-hint">
                        <p>Подробный план и чек-листы доступны во вкладке &quot;Таланты&quot;</p>
                        <button className="btn-secondary" onClick={() => setActiveTab('talents')}>
                          {'Перейти к талантам'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Если нет ИПР, показываем сообщение
                    <div className="no-ipr-message">
                      <p>У вас еще нет созданных планов развития.</p>
                      <p>
                        Чтобы создать план, перейдите на вкладку &quot;Таланты&quot; и нажмите
                        кнопку &quot;Создать план развития&quot; для выбранного таланта.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Модальное окно для создания ИПР */}
      {selectedTalent && showIPRModal && (
        <IPRModal
          talent={selectedTalent}
          isOpen={showIPRModal}
          onClose={handleCloseIPRModal}
          onSubmit={handleIPRSubmit}
          loading={isCreatingIPR}
        />
      )}
    </div>
  )
}

export default ProfilePage
